import { Box, Typography } from '@customink/pigment-react/';
import {
  BULK_SPLIT_SHIP,
  useDistributedOrder,
  useProject,
} from '@customink/project-service-react-components';
import type { ShippingType } from '@customink/project-service-types';
import { SHIPPING_TYPES } from '@customink/project-service-types';
import { useEffect, useState } from 'react';
import type { ChangeEventHandler } from 'react';

import Option from './DeliveryOption';
import ShipToMultiple from './ShipToMultiple';
import TurnTimeSelect from './TurnTimeSelect';
import { type DeliveryMethod, DeliveryMethods } from './util';
import { CustomerCareLink } from '../CustomerCareLink';

import { DeliveryOptionHeader } from '@/components/DeliveryOptions/DeliveryOptionHeader';
import { LiveEngageLink } from '@/components/LiveEngageLink';
import { LivePersonId } from '@/constants';
import {
  useFirstDesignCid,
  useHasMultipleDesigns,
  useHasPersonalization,
} from '@/hooks/Design';
import { useDistributedShipStrategy } from '@/hooks/Project';
import { useHasAnySinglesQuantities } from '@/hooks/Project/useHasAnySinglesQuantities';

const { MIN_ADDRESS_COUNT } = BULK_SPLIT_SHIP;

type DeliveryOptionsProps = {
  showTitle?: boolean;
};

const shippingTypeDeliveryMethodMap = {
  [SHIPPING_TYPES.BULK]: DeliveryMethods.SHIP_TO_ONE,
  [SHIPPING_TYPES.INDIVIDUAL]: DeliveryMethods.SHIP_TO_MULTIPLE,
};

export default function DeliveryOptions({
  showTitle = true,
}: DeliveryOptionsProps) {
  const { data: project } = useProject();

  const originalAddressCount = project?.distributedOrder?.numberOfAddresses;
  const [addressCount, setAddressCount] = useState<number>(
    originalAddressCount || MIN_ADDRESS_COUNT,
  );

  const shippingType =
    project?.configuration?.shippingType || SHIPPING_TYPES.BULK;

  const initialDeliveryMethod = shippingTypeDeliveryMethodMap[shippingType];

  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<DeliveryMethod>(initialDeliveryMethod);

  const [updateProjectDistributedOrder] = useDistributedOrder();

  const designCid: string = useFirstDesignCid();
  const hasMultipleDesigns: boolean = useHasMultipleDesigns();
  const hasPersonalization: boolean = useHasPersonalization();
  const hasAnySinglesQuantities: boolean = useHasAnySinglesQuantities();

  const [
    {
      ineligibleReason: distributedShipIneligibleReason,
      isEligible: isDistributedShipEligible,
      shippingType: distributedShippingType,
    },
  ] = useDistributedShipStrategy();

  // this may not be neccessary after all the other BSS stuff has been implemented?
  useEffect(() => {
    if (
      (hasAnySinglesQuantities || hasPersonalization) &&
      selectedDeliveryMethod !== DeliveryMethods.SHIP_TO_ONE
    ) {
      setSelectedDeliveryMethod(DeliveryMethods.SHIP_TO_ONE);
      updateProjectDistributedOrder({
        shippingType: SHIPPING_TYPES.BULK,
        numberOfAddresses: addressCount,
      });
    }
  }, [
    addressCount,
    hasAnySinglesQuantities,
    hasPersonalization,
    selectedDeliveryMethod,
    updateProjectDistributedOrder,
  ]);

  const onChangeAddressCount = (newAddressCount: number) => {
    setAddressCount(newAddressCount);
    updateProjectDistributedOrder({
      shippingType: SHIPPING_TYPES.INDIVIDUAL,
      numberOfAddresses: newAddressCount,
    });
  };

  const handleSelectDeliveryMethod: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const newMethod = event.target.value as DeliveryMethod;

    const shippingType: ShippingType | null = {
      [DeliveryMethods.SHIP_TO_ONE]: SHIPPING_TYPES.BULK,
      [DeliveryMethods.SHIP_TO_MULTIPLE]: distributedShippingType,
    }[newMethod];

    if (!shippingType) return;

    setSelectedDeliveryMethod(newMethod);

    updateProjectDistributedOrder({
      shippingType,
      numberOfAddresses: addressCount,
    });
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '652px',
      }}
    >
      {!!showTitle && (
        <Typography
          component="div"
          sx={{ marginTop: '8px', marginBottom: '8px' }}
          variant="heading4"
        >
          Delivery Options
        </Typography>
      )}

      <Option
        deliveryMethod={DeliveryMethods.SHIP_TO_ONE}
        label={<DeliveryOptionHeader title="Ship to one address" />}
        onSelect={handleSelectDeliveryMethod}
        selected={selectedDeliveryMethod === DeliveryMethods.SHIP_TO_ONE}
      >
        {!hasMultipleDesigns && <TurnTimeSelect designCid={designCid} />}
      </Option>

      <Option
        deliveryMethod={DeliveryMethods.SHIP_TO_MULTIPLE}
        disabled={!isDistributedShipEligible}
        label={
          <>
            <DeliveryOptionHeader title="Ship to multiple addresses" />
            {distributedShipIneligibleReason && (
              <Typography
                component="div"
                sx={{ marginY: '8px' }}
                variant="uiTextSmall"
              >
                {distributedShipIneligibleReason}
              </Typography>
            )}
          </>
        }
        onSelect={handleSelectDeliveryMethod}
        selected={selectedDeliveryMethod === DeliveryMethods.SHIP_TO_MULTIPLE}
      >
        <>
          {distributedShippingType === SHIPPING_TYPES.INDIVIDUAL && (
            <>
              {hasMultipleDesigns ? (
                <Typography paddingBottom="4px">
                  Please call us at
                  <CustomerCareLink />
                  <LiveEngageLink liveEngageId={LivePersonId} prefix="or">
                    start a chat
                  </LiveEngageLink>
                </Typography>
              ) : (
                <ShipToMultiple
                  addressCount={addressCount}
                  onChangeAddressCount={onChangeAddressCount}
                />
              )}
            </>
          )}
        </>
      </Option>
    </Box>
  );
}
